import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Layout,
  Row,
  Typography,
  Modal,
  message,
  Tabs,
  Popconfirm,
} from "antd";
import type { InputRef } from "antd";
import moment from "moment";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { SearchOutlined, MailOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import debounce from "lodash.debounce";
import type { ColumnType } from "antd/es/table";
import {
  FETCH_ALL_REMINDERS,
  FETCH_SIGNED_DOCUMENTS,
  SEND_REMINDER_EMAIL,
} from "../../services/apiEndPoints";
import { IDatabase } from "../types/document.types";
import { imageInfo, snakeToCapitalCase } from "../upload/DualUpload";
import { getTagColor } from "./reminder.utils";
import { isPdf } from "../document/DocumentTableComponent";
import axios from "../../services/axiosInstance";

const { Title, Link } = Typography;
const { Content } = Layout;

const ReminderComponent = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [data, setData] = useState<IDatabase[]>([]);
  const searchInput = useRef<InputRef>(null);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [emailLoadingId, setEmailLoadingId] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [activeTab, setActiveTab] = useState<string>("30");


  const today = moment().startOf("day");

  /* ---------------- API ---------------- */

  const fetchSignedUrl = async (s3Url: string) => {
    const response = await axios.get(
      `${FETCH_SIGNED_DOCUMENTS}?s3Url=${encodeURIComponent(s3Url)}`
    );
    if (response?.data?.results) return response.data.results;
    throw new Error("Failed to fetch signed URL");
  };

  const fetchDocuments = async () => {
    const response = await axios.get(FETCH_ALL_REMINDERS);
    if (response?.data?.results) setData(response.data.results);
  };

  const sendEmailReminder = async (id: string) => {
    try {
      setEmailLoadingId(id);
      await axios.post(SEND_REMINDER_EMAIL(id), {});
      message.success("Reminder email sent successfully");
    } catch {
      message.error("Failed to send reminder email");
    } finally {
      setEmailLoadingId(null);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  /* ---------------- SEARCH ---------------- */

  const handleSearch = useMemo(
    () =>
      debounce((selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
      }, 300),
    []
  );

  const getColumnSearchProps = (
    dataIndex: keyof IDatabase
  ): ColumnType<IDatabase> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${String(dataIndex)}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8 }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters?.()}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        .toLowerCase()
        .includes(String(value).toLowerCase()) ?? false,
    render: (text: string) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text || ""}
        />
      ) : (
        text
      ),
  });

  const handleBulkDownload = () => {
    const doc = new jsPDF("l", "mm", "a4");

    const tabMap: Record<string, IDatabase[]> = {
      "30": last30Days,
      "60": last60Days,
      "rest": rest,
      "expired": expired,
      "renewed": renewed,
    };

    const source = tabMap[activeTab] || [];

    if (!source.length) {
      message.warning("No records to download");
      return;
    }

    const titleMap: Record<string, string> = {
      "30": "Documents Expiring in 30 Days",
      "60": "Documents Expiring in 31–60 Days",
      "rest": "Documents Expiring Later",
      "expired": "Expired Documents",
      "renewed": "Renewed Documents",
    };

    doc.setFontSize(16);
    doc.text(titleMap[activeTab], 14, 16);

    doc.setFontSize(10);
    doc.text(
      `Generated on: ${moment().format("MMM D, YYYY")}`,
      14,
      22
    );

    const tableData = source.map((item, index) => ({
      index: index + 1,
      uniqId: item.uniqId || "-",
      type: item.documentType || "-",
      company: item.companyName || "-",
      location: item.location || "-",
      expiry: item.expiryDate
        ? moment(item.expiryDate).format("MMM D, YYYY")
        : "-",
      status: item.isRenewed
        ? "Renewed"
        : item.expiryDate &&
          moment(item.expiryDate).diff(today, "days") < 0
          ? "Expired"
          : "Active",
      details: typeof item.meta === "string"
        ? item.meta
        : item.meta && typeof item.meta === "object"
          ? Array.isArray(item.meta)
            ? item.meta.join(", ")
            : Object.entries(item.meta)
              .map(([key, value]) => `${snakeToCapitalCase(key)}: ${value}`)
              .join(", ")
          : "-",
    }));

    autoTable(doc, {
      startY: 28,
      head: [
        [
          "#",
          "Document No",
          "Type",
          "Company",
          "Location",
          "Expiry Date",
          "Status",
          "Details"
        ],
      ],
      body: tableData.map((row) => [
        row.index,
        row.uniqId,
        row.type,
        row.company,
        row.location,
        row.expiry,
        row.status,
        row.details || "-",
      ]),
      styles: {
        fontSize: 9,
        cellPadding: 3,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [22, 119, 255],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250],
      },
      margin: { left: 14, right: 14 },
    });

    doc.save(`${titleMap[activeTab].replace(/\s+/g, "_")}.pdf`);
  };


  /* ---------------- COLUMNS ---------------- */

  const columns: ColumnType<IDatabase>[] = useMemo(
    () => [
      {
        title: "#",
        width: 70,
        align: "center",
        render: (_: any, __: any, index: number) =>
          (pagination.current - 1) * pagination.pageSize + index + 1,
      },
      {
        title: "Document No",
        dataIndex: "uniqId",
        key: "uniqId",
        width: 160,
        render: (value?: string) => value || "-",
      },
      {
        title: "Type",
        dataIndex: "documentType",
        key: "documentType",
        sorter: (a: IDatabase, b: IDatabase) =>
          (a.documentType || "").localeCompare(b.documentType || ""),
        ...getColumnSearchProps("documentType"),
      },
      {
        title: "Expiry Date",
        dataIndex: "expiryDate",
        key: "expiryDate",
        width: 160,
        align: "center" as const,
        render: (date: string) => {
          if (!date) return "-";
          const daysLeft = moment(date).diff(today, "days");

          let color: "green" | "orange" | "red" = "green";
          if (daysLeft <= 30) color = "red";
          else if (daysLeft <= 60) color = "orange";

          return (
            <Tag color={color} style={{ fontWeight: 500 }}>
              {moment(date).format("MMM D, YYYY")}
            </Tag>
          );
        },
      },
      {
        title: "Document",
        dataIndex: "s3Url",
        key: "s3Url",
        width: 100,
        align: "center" as const,
        render: (s3Url: string) =>
          s3Url ? (
            <Link
              onClick={async () => {
                try {
                  setLoadingPreview(true);
                  const url = await fetchSignedUrl(s3Url);
                  setSignedUrl(url);
                  setIsPreviewOpen(true);
                } catch {
                  message.error("Failed to load document");
                } finally {
                  setLoadingPreview(false);
                }
              }}
            >
              View
            </Link>
          ) : (
            "-"
          ),
      },
      {
        title: "Company Name",
        dataIndex: "companyName",
        key: "companyName",
        sorter: (a: IDatabase, b: IDatabase) =>
          (a.companyName || "").localeCompare(b.companyName || ""),
        ...getColumnSearchProps("companyName"),
        render: (value?: string) => value || "-",
      },
      {
        title: "Location",
        dataIndex: "location",
        key: "location",
        sorter: (a: IDatabase, b: IDatabase) => (a.location || "").localeCompare(b.location || ""),
        ...getColumnSearchProps("location"),
        render: (location?: string) => location || "-",
      },
      {
        title: "Details",
        dataIndex: "meta",
        key: "meta",
        width: 420,
        render: (meta: Record<string, any> | string[]) => {
          if (!meta) return "-";

          if (typeof meta === "object" && !Array.isArray(meta)) {
            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {imageInfo(meta).map(({ key, value }) => (
                  <Tag
                    key={key}
                    color={getTagColor(key)}
                    style={{ fontSize: 10 }}
                  >
                    <b>{snakeToCapitalCase(key)}</b>: {String(value)}
                  </Tag>
                ))}
              </div>
            );
          }

          if (Array.isArray(meta)) {
            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {meta.map((item) => (
                  <Tag key={item}>{item}</Tag>
                ))}
              </div>
            );
          }

          return "-";
        },
      },
      {
        title: "By",
        dataIndex: "uploadedBy",
        key: "uploadedBy",
        width: 150,
        ellipsis: true,
      },
      {
        title: "Actions",
        key: "actions",
        width: 160,
        align: "center" as const,
        render: (_: any, record: IDatabase) => {
          const isRenewed = record.isRenewed;

          return (
            <Popconfirm
              title={
                isRenewed
                  ? "Renewed documents do not require reminders"
                  : "Send email reminder?"
              }
              onConfirm={() =>
                !isRenewed && sendEmailReminder(record.documentId)
              }
              disabled={isRenewed}
            >
              <Button
                icon={<MailOutlined />}
                type="primary"
                ghost
                disabled={isRenewed}
                loading={!isRenewed && emailLoadingId === record.documentId}
              >
                Email Reminder
              </Button>
            </Popconfirm>
          );
        },
      },
    ],
    [searchText, searchedColumn, emailLoadingId]
  );

  /* ---------------- BUCKETS ---------------- */

  const { last30Days, last60Days, rest, expired, renewed } = useMemo(() => {
    const expired: IDatabase[] = [];
    const last30Days: IDatabase[] = [];
    const last60Days: IDatabase[] = [];
    const rest: IDatabase[] = [];
    const renewed: IDatabase[] = [];

    data.forEach((item) => {
      if (item.isRenewed) {
        renewed.push(item);
        return;
      }

      if (!item.expiryDate) {
        rest.push(item);
        return;
      }

      const diff = moment(item.expiryDate).diff(today, "days");

      if (diff < 0) expired.push(item);
      else if (diff <= 30) last30Days.push(item);
      else if (diff <= 60) last60Days.push(item);
      else rest.push(item);
    });

    return { last30Days, last60Days, rest, expired, renewed };
  }, [data, today]);

  /* ---------------- UI ---------------- */

  const tableProps = {
    columns,
    size: "middle" as const,
  };

  return (
    <Layout style={{ padding: 16, marginTop: 16 }}>
      <Content style={{ padding: 8, overflow: "visible" }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            Expiry Reminders
          </Title>
        </Row>


        <Tabs
          defaultActiveKey="30"
          size="large"
          activeKey={activeTab}
          onChange={setActiveTab}
          tabBarExtraContent={{
            right: (
              <Button
                type="primary"
                onClick={handleBulkDownload}
              >
                Download
              </Button>
            ),
          }}
        >
          {[
            ["30", `Expiring in 30 Days (${last30Days.length})`, last30Days],
            ["60", `Expiring 31–60 Days (${last60Days.length})`, last60Days],
            ["rest", `Expiring Later (${rest.length})`, rest],
            ["expired", `Expired (${expired.length})`, expired],
            ["renewed", `Renewed (${renewed.length})`, renewed],
          ].map(([key, label, source]) => (
            <Tabs.TabPane tab={label as string} key={key as string}>
              <div
                style={{
                  background: "#fff",
                  padding: 16,
                  borderRadius: 8,
                  fontSize: 14
                }}
              >
                <Table rowKey="id" dataSource={source as IDatabase[]} sticky
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    showSizeChanger: true,
                  }}
                  onChange={(paginationInfo) => {
                    setPagination({
                      current: paginationInfo.current || 1,
                      pageSize: paginationInfo.pageSize || 10,
                    });
                  }}
                  scroll={{ x: "max-content" }} {...tableProps} />
              </div>
            </Tabs.TabPane>
          ))}
        </Tabs>

      </Content>

      <Modal
        open={isPreviewOpen}
        title="Document Preview"
        onCancel={() => {
          setIsPreviewOpen(false);
          setSignedUrl(null);
        }}
        width="80%"
        footer={[
          <Button
            key="download"
            type="primary"
            disabled={!signedUrl}
            onClick={() => {
              if (!signedUrl) return;
              const link = document.createElement("a");
              link.href = signedUrl;
              link.target = "_blank";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            Download
          </Button>,
          <Button key="close" onClick={() => setIsPreviewOpen(false)}>
            Close
          </Button>,
        ]}
      >
        {loadingPreview ? (
          <div style={{ textAlign: "center" }}>Loading...</div>
        ) : signedUrl && isPdf(signedUrl) ? (
          <iframe src={signedUrl} width="100%" style={{ height: "75vh" }} />
        ) : (
          <img
            src={signedUrl || ""}
            alt="Preview"
            style={{
              maxWidth: "100%",
              maxHeight: "75vh",
              display: "block",
              margin: "0 auto",
            }}
          />
        )}
      </Modal>
    </Layout>
  );
};

export default ReminderComponent;
